import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/** Pick another KB article to link to. The link stores the article id, so it
 *  keeps working even if the target's URL slug changes later. */
export function KbArticleLinkDialog({ open, onOpenChange, onPick, snippetsOnly = false, title = "Link to a KB article" }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/kb/manage/articles", {
        params: { q, limit: 25, sort: "name", content_type: snippetsOnly ? "snippet" : "" },
      })
        .then((r) => setRows(r.data.articles || []))
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, snippetsOnly]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="kb-article-link-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <Input autoFocus data-testid="kb-article-link-search" value={q}
            placeholder={snippetsOnly ? "Search reusable blocks…" : "Search articles…"}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 pl-8 text-sm" />
        </div>
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
        ) : (
          <ul className="max-h-[50vh] space-y-1 overflow-y-auto">
            {rows.length === 0 && (
              <li className="py-8 text-center text-sm text-slate-400 dark:text-slate-500">
                {snippetsOnly ? "No snippets yet — create one from Manage articles → New snippet." : "No matches."}
              </li>
            )}
            {rows.map((a) => (
              <li key={a.article_id}>
                <button type="button" data-testid={`kb-article-link-pick-${a.slug}`}
                  onClick={() => { onPick(a); onOpenChange(false); }}
                  className="w-full rounded-md px-3 py-2 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800">
                  <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">{a.name}</span>
                  <span className="block truncate text-xs text-slate-400 dark:text-slate-500">
                    /kb/a/{a.slug}{a.is_draft ? " · draft" : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
